import React, { useEffect, useState, useContext } from "react";

import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import useAbortController from "../../../Common/hooks/useAbortController";
import Loading from "../../../Common/Loading";
import NoResultsGIF from "../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import { useAccessStatus } from "../../../grace-period/hooks/useAccessStatus";
import ExportDataModal from "../../ExportDataModal/ExportDataModal";
import SampleFunctionContext from "../../SampleFunctionContext";
import SampleShareModal from "../../SampleSubmissionModal/SampleShareModal";
import SampleSubmissionConfirmModal from "../../SampleSubmissionModal/SampleSubmissionConfirmModal";
import FloatingButton from "../Common/FloatingButton";
import InviteModal from "../Common/Invite/InviteModal";
import { sampleHasParentSample, submissionHasRetestSample } from "../helper";
import ActivityHistoryModal from "./ActivityHistoryModal";
import SampleIssueActionModal from "./SampleIssueActionModal";
import SubmissionDetailsModal from "./SubmissionDetailsModal";
import { SUBMISSION_DETAILS_FETCH_DEFAULTS } from "./SubmissionDetailsSamplesTable";
import { hasSubmissionExpiry, hasSubmissionTrackingInfo } from "./submissionExpiryUtils";
import {
  buildEditableSampleFromIssue,
  allSamplesHaveIssues,
  findIssueSampleInList,
  getIssueSampleUniqueIdentifier,
  getIssueSampleTests,
  getSamplesWithIssues,
  hasSampleIssues,
  isExpandableSampleIssues,
  mergeEditableIssueSample,
  normalizeIssueType,
} from "./submissionIssueUtils";
import SubmissionShippedFlowModal from "./SubmissionShippedFlowModal";
import SubmissionsSearch from "./SubmissionsSearch";
import SubmittedSubmissionIssueChildRow from "./SubmittedSubmissionIssueChildRow";
import SubmittedSubmissionRow from "./SubmittedSubmissionRow";

import { deleteFileFromS3WithPresigned } from "../../../../utils/helpers";
import { hasPermission, permissions, canExportSampleSubmissionData } from "../../../../utils/Permissions";
import { convertUtcToLocal } from "../../../Common/utils/dateUtils";
import { buildCreateSampleSubmissionPath } from "../../utils";

import {
  getSubmittedSubmissionsInfo,
  deleteSubmission,
  getSampleFields,
  getDraftSubmissionsInfo,
  deleteDraft,
} from "../../../../actions/sampleSubmission";

import "../../SampleSubmission.css";
import "../MainPage.css";
import "./Submissions.css";

import pumpkinGIF from "../../../../assets/images/environment/pumpkin.gif";

export default function Submissions({
  setShowSubmissionForm,
  setSubmissionEditingSample,
  setSubmissionAddingSamples,
  setDraftEditing,
  loadingSubmissions,
  setLoadingSubmissions,
  submittedSubmissions,
  setSubmittedSubmissions,
  isInitialData,
  setSampleCategory,
  setShowUpdateOnboardingModal,
}) {
  const submittedSubmissionsScrollbarRef = React.useRef();
  const [search, setSearch] = useState("");
  const [searchByField, setSearchByField] = useState("all");
  const [dateFilter, setDateFilter] = useState({
    dateFromUTC: "", dateToUTC: "", dateFromLocal: "", dateToLocal: "",
  });
  const [submittedByLst, setSubmittedByLst] = useState([]);
  const [loadingNewContent, setLoadingNewContent] = useState(false);
  const [showSubmissionDetailsModal, setShowSubmissionDetailsModal] = useState(false);
  const [showShippedFlowModal, setShowShippedFlowModal] = useState(false);
  const [shippedFlowSubmission, setShippedFlowSubmission] = useState(null);
  const [showActivityHistoryModal, setShowActivityHistoryModal] = useState(false);
  const [activityHistorySubmission, setActivityHistorySubmission] = useState(null);
  const [expandedIssueSubmissionId, setExpandedIssueSubmissionId] = useState(null);
  const [showSampleIssueActionModal, setShowSampleIssueActionModal] = useState(false);
  const [sampleIssueFlowSubmission, setSampleIssueFlowSubmission] = useState(null);
  const [sampleIssueFlowSample, setSampleIssueFlowSample] = useState(null);
  const [paginationInfo, setPaginationInfo] = useState({
    prevPageLink: "", nextPageLink: "", currentPage: "1", totalPages: "1",
  });
  const [submissionClicked, setSubmissionClicked] = useState({});
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [scheduledSubmissionToDelete, setScheduledSubmissionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [scheduledDeleteLoading, setScheduledDeleteLoading] = useState(false);
  const [scheduledActionLoadingId, setScheduledActionLoadingId] = useState(null);
  const [sampleFilterCategory, setSampleFilterCategory] = useState("");
  const [submitBy, setSubmitBy] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [issuesFilter, setIssuesFilter] = useState("");
  const [deletedSamplesFilter, setDeletedSamplesFilter] = useState([]);
  const [shareSubmission, setShareSubmission] = useState(false);
  const [exportData, setExportData] = useState(false);
  const [sampleLinkFieldsConfig, setSampleLinkFieldsConfig] = useState({
    link_report: [],
    link_pattern_fields: [],
  });
  const [sampleFields, setSampleFields] = useState([]);
  const [sampleFieldsInfo, setSampleFieldsInfo] = useState({});
  const [loadingFields, setLoadingFields] = useState(true);
  const [shareSampleInfo, setShareSampleInfo] = useState(false);
  const [shareSampleType, setShareSampleType] = useState("");
  const { setNewAbortController, clearAbortController } = useAbortController();
  const { setOnAddBackButton } = useContext(SampleFunctionContext);
  const history = useHistory();
  const { areResultsLocked } = useAccessStatus();
  const [filterTagsContainer, setFilterTagsContainer] = useState(null);

  /** Get list of submitted submissions and pagination info.
     * Each submitted submission has a different status code string which represents its status:
     * "-1": "Submitted", "0": "Samples Received", "1": "Testing in Progress", "2": "Testing Complete".
     * Each submitted submission has a product spec string to represent its displayed product spec:
     * "Added": "Spec Added", "Pending": "Spec Incomplete".
     * Each sample has a specs flag to represent its status:
     * 0: "In Process", "1": "In Spec", "2": "Out of Spec", "3": "Spec Incomplete", "4": "In Process (Retest)", "5": "In Spec (Retest)", "6": "Out of Spec (Retest)"
     * @param {Number} page page being fetched
     * @param {String} searchValue
     * @param {string} searchBy one of all/submission_name/sample_id/po
     * @param {Object} datesObj { dateFromUTC, dateFromLocal, dateToUTC, dateToLocal }
     * @param {Boolean} isInitial if this is the first call to fetch submissions
     * */
  const getSubmissions = async (page = 1, searchValue = "", searchBy = "all", datesObj = {}, isInitial = false, submittedBy = "", field = "", sampleCategory = "", status = "", issues = "", deletedSamples = []) => { // eslint-disable-line
    if (page > 1) {
      setLoadingNewContent(true);
    } else {
      setLoadingSubmissions(true);
      submittedSubmissionsScrollbarRef.current.scrollToTop();
    }

    const newAbortController = setNewAbortController();
    const {
      dateFromUTC, dateFromLocal, dateToUTC, dateToLocal,
    } = datesObj;
    const payload = {
      submission_id: "",
      page,
      search_value: searchValue,
      search_by: searchBy,
      status: status ?? "",
      date_from: dateFromUTC ?? "",
      date_to: dateToUTC ?? "",
      user_selected_date_from: dateFromLocal ?? "",
      user_selected_date_to: dateToLocal ?? "",
      submitted_by: submittedBy ?? "",
      sample_category: sampleCategory,
      has_issues: issues ?? "",
      deleted_by: Array.isArray(deletedSamples) ? deletedSamples.join(",") : (deletedSamples ?? ""),
    };
    const {
      success: submittedSuccess,
      message: submittedMessage, next_page_link, prev_page_link,
      total_page, result: submitted_submissions, submitted_by_lst,
    } = await getSubmittedSubmissionsInfo(payload, newAbortController.signal);

    if (newAbortController.signal.aborted) {
      /* Set pagination loading state to false (the current call in progress won't set this to false when finished) */
      if (page > 1) {
        setLoadingNewContent(false);
      }
      return;
    }

    if (submittedSuccess) {
      setPaginationInfo({
        prevPageLink: prev_page_link, nextPageLink: next_page_link, totalPages: total_page, currentPage: page,
      });
      setSubmittedSubmissions(page > 1 ? [...submittedSubmissions, ...submitted_submissions] : submitted_submissions);
      setSubmittedByLst(submitted_by_lst);
    } else {
      toast.error(submittedMessage);
      if (page > 1) {
        submittedSubmissionsScrollbarRef.current.scrollTop -= 100; // prevent infinitely firing scroll api call
      } else {
        setSubmittedSubmissions([]);
      }
    }

    /** Used to distinguish between if a company actually has no submissions or search result is empty */
    if (isInitialData) {
      isInitialData.current = isInitial;
    }

    clearAbortController();

    if (page > 1) {
      /* Fix safari bug where api is called repeatedly.
        Scroll scrollbar 4 pixels from bottom so it's outside of the tolerance range (3 pixels) and doesn't trigger the api call */
      submittedSubmissionsScrollbarRef.current.scrollTop -= 4;
      setLoadingNewContent(false);
    } else {
      setLoadingSubmissions(false);
      if (submittedSubmissionsScrollbarRef.current) {
        submittedSubmissionsScrollbarRef.current.scrollToTop();
      }
    }
  };

  /** on component mount - get submitted submissions lists */
  useEffect(() => {
    getSubmissions(1, "", searchByField, {}, true);
    setLoadingFields(false);
  }, []); // eslint-disable-line

  /** Collapse issue expand when refreshed data is no longer multi-issue */
  useEffect(() => {
    if (!expandedIssueSubmissionId) return;
    const expandedSubmission = submittedSubmissions.find(
      (item) => item.submit_id === expandedIssueSubmissionId,
    );
    if (expandedSubmission && !isExpandableSampleIssues(expandedSubmission)) {
      setExpandedIssueSubmissionId(null);
    }
  }, [submittedSubmissions, expandedIssueSubmissionId]);

  const enrichIssueSampleForResolve = async (submission, issueSample) => {
    let sample = buildEditableSampleFromIssue(issueSample, submission);

    const { success: draftSuccess, result: draftResult } = await getDraftSubmissionsInfo(submission.submit_id);
    if (draftSuccess && Array.isArray(draftResult?.samples)) {
      let draftSample = findIssueSampleInList(issueSample, draftResult.samples);
      if (!draftSample && draftResult.samples.length === 1) {
        draftSample = draftResult.samples[0];
      }
      sample = mergeEditableIssueSample(sample, draftSample);
    }

    if (!getIssueSampleTests(sample).length || !sample.sample_type) {
      const { success, result } = await getSubmittedSubmissionsInfo({
        ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
        submission_id: submission.submit_id,
        sample_category: submission.sample_category,
      });
      if (success && Array.isArray(result?.samples_list)) {
        const detailSample = findIssueSampleInList(issueSample, result.samples_list);
        sample = mergeEditableIssueSample(sample, detailSample);
      }
    }

    return sample;
  };

  const handleOpenSampleIssue = (submission, issueSample = null) => {
    const issues = getSamplesWithIssues(submission);
    const selectedIssue = issueSample || (issues.length === 1 ? issues[0] : null);
    if (!selectedIssue) return;

    setSampleIssueFlowSubmission(submission);
    setSampleIssueFlowSample(selectedIssue);
    setShowSampleIssueActionModal(true);
  };

  const apiGetSampleFields = async (category) => {
    const result = await getSampleFields(category);
    if (result.success) {
      if (result.fields && result.fields.length) {
        const fieldsMap = {};
        result.fields.forEach((fieldObj) => {
          fieldsMap[fieldObj.json_field] = fieldObj;
        });
        /** Only show fields that are not display "2", keep the order sent by backend */
        const sampleSubmissionFields = result.fields
          .filter(({ display }) => display !== "2")
          .map(({ json_field }) => json_field);
        setSampleFieldsInfo(fieldsMap || {});
        setSampleFields(sampleSubmissionFields);
        setSampleLinkFieldsConfig({
          link_report: result.link_report ?? [],
          link_pattern_fields: result.link_pattern_fields ?? [],
        });
      } else {
        toast.error("Fields are missing or empty");
      }
    } else {
      toast.error(result.message);
    }
  };
  const handleOpenExpiryFlow = async (submission) => {
    if (!hasSubmissionExpiry(submission) || hasSubmissionTrackingInfo(submission)) return;

    let enrichedSubmission = submission;
    const { success, result } = await getSubmittedSubmissionsInfo({
      ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
      submission_id: submission.submit_id,
      sample_category: submission.sample_category,
    });
    if (success && Array.isArray(result?.samples_list) && result.samples_list.length) {
      enrichedSubmission = {
        ...submission,
        samples_list: result.samples_list,
      };
    }

    setShippedFlowSubmission(enrichedSubmission);
    setShowShippedFlowModal(true);
  };

  /** Update id of the clicked submissiom, open submission details modal, pass the id to the modal */
  const handleClickSubmission = async (submission) => {
    if (hasSampleIssues(submission) && !isExpandableSampleIssues(submission)) {
      handleOpenSampleIssue(submission);
      return;
    }

    if (allSamplesHaveIssues(submission) && isExpandableSampleIssues(submission)) {
      return;
    }

    apiGetSampleFields(submission.sample_category);
    setSubmissionClicked(submission);
    setSampleCategory(submission.sample_category);
    setShowSubmissionDetailsModal(true);
  };

  const handleToggleIssueExpand = (submission) => {
    setExpandedIssueSubmissionId((current) => (
      current === submission.submit_id ? null : submission.submit_id
    ));
  };

  const handleEditIssueSample = async (submission, issueSample) => {
    const {
      submit_id, submit_name, submit_date, po, status, sample_category,
    } = submission;

    const sample = await enrichIssueSampleForResolve(submission, issueSample);

    setSampleCategory(sample_category);
    setSubmissionEditingSample({
      submit_id,
      submit_name,
      status,
      submission_date: convertUtcToLocal(submit_date),
      sample,
      po: po ?? "",
      sample_category,
      issueFlow: true,
      issueType: normalizeIssueType(issueSample?.issue_type),
    });
    setShowSubmissionForm(true);
    history.push(buildCreateSampleSubmissionPath(sample_category));
  };

  const handleOpenActivityHistory = (submission) => {
    setActivityHistorySubmission(submission);
    setShowActivityHistoryModal(true);
  };

  /** If we should load the next page, scroll to bottom */
  useEffect(() => {
    if (loadingNewContent) {
      submittedSubmissionsScrollbarRef.current.scrollToBottom();
    }
  }, [loadingNewContent]);

  /**
   * On scroll stop, if scroll bar is at the bottom, load the next page of rows
   * @param  {Event} e on scroll stop event
   */
  const handleScrollToBottom = (e) => {
    const handlePageChangeScroll = async () => {
      const {
        nextPageLink, currentPage, totalPages,
      } = paginationInfo;
      const isLastPage = parseInt(currentPage) === parseInt(totalPages);

      if (!isLastPage && !loadingNewContent && !loadingSubmissions) {
        const nextPage = nextPageLink ? nextPageLink.split("page=")[1].split("&")[0] : "1";
        getSubmissions(nextPage, search, searchByField, dateFilter, false, submitBy, searchByField, sampleFilterCategory, statusFilter, issuesFilter, deletedSamplesFilter);
      }
    };

    const tolerance = 3;
    const bottom = Math.abs(Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight)) <= tolerance;
    if (bottom) {
      handlePageChangeScroll();
    }
  };

  /** Refresh the submissions list (get the first page and scroll the table to the top) */
  const refreshSubmissionsList = async () => {
    await getSubmissions(
      1,
      search,
      searchByField,
      dateFilter,
      false,
      submitBy,
      searchByField,
      sampleFilterCategory,
      statusFilter,
      issuesFilter,
      deletedSamplesFilter,
    );
    submittedSubmissionsScrollbarRef.current.scrollToTop();
  };

  /** After issue delete/resubmit — collapse expand state and refresh table */
  const handleIssueFlowSuccess = async () => {
    setExpandedIssueSubmissionId(null);
    await refreshSubmissionsList();
  };

  /**
   * Set submissionEditingSample object in parent, show submission form
   * @param {Object} submission { submit_id, submit_name, submit_date, po, status }
   * @param {Object} sample { [fields], sample_id, tests_list}
   */
  const handleEditSample = async (submission, sample) => {
    if (sampleHasParentSample(sample)) {
      return;
    }

    const {
      submit_id, submit_name, submit_date, po, status,
    } = submission;

    setSampleCategory(submission.sample_category);
    setSubmissionEditingSample({
      submit_id,
      submit_name,
      status,
      submission_date: convertUtcToLocal(submit_date),
      sample: { ...sample },
      po: po ?? "",
      sample_category: submission.sample_category,
    });
    setShowSubmissionForm(true);
    history.push(buildCreateSampleSubmissionPath(submission.sample_category));
  };
  /**
   * Set submissionAddingSamples object in parent, show submission form
   * @param {Object} submission { submit_id, submit_name, submit_date, po, status }
   */
  const handleAddSamples = async (submission) => {
    if (submissionHasRetestSample(submission)) {
      return;
    }

    const {
      submit_id, submit_name, submit_date, po, status,
      // comment,
    } = submission;
    setSampleCategory(submission.sample_category);
    setSubmissionAddingSamples({
      submit_id,
      submit_name,
      status,
      submission_date: convertUtcToLocal(submit_date),
      po: po ?? "",
      // comment,
    });
    setOnAddBackButton(true);
    setShowSubmissionForm(true);
    history.push(buildCreateSampleSubmissionPath(submission.sample_category));
  };

  /**
   * Set submissionToDelete and show confirmation modal
   * @param {Object} submission { submit_id, submit_name, submit_date, po, status }
   */
  const handleDeleteSubmission = async (submission) => {
    setSubmissionToDelete(submission);
  };

  const handleEditScheduledSubmission = async (submission) => {
    if (submissionHasRetestSample(submission)) {
      return;
    }

    const submissionId = submission.submit_id;
    setScheduledActionLoadingId(submissionId);

    // List rows may omit samples_list — confirm parent_sample from submission details
    if (!(submission.samples_list && submission.samples_list.length)) {
      const { success: detailSuccess, result: detailResult } = await getSubmittedSubmissionsInfo({
        ...SUBMISSION_DETAILS_FETCH_DEFAULTS,
        submission_id: submissionId,
        sample_category: submission.sample_category,
      });
      if (detailSuccess && submissionHasRetestSample(detailResult)) {
        setScheduledActionLoadingId(null);
        return;
      }
    }

    const { success, message, result } = await getDraftSubmissionsInfo(submissionId);
    if (success) {
      const scheduleDateIso = submission.scheduled_date ?? submission.schedule_date;
      setDraftEditing({
        submission_id: submissionId,
        submission_name: submission.submit_name,
        submission_date: scheduleDateIso ? convertUtcToLocal(scheduleDateIso) : undefined,
        ...result,
        sample_category: submission.sample_category,
      });
      setShowSubmissionDetailsModal(false);
      setSampleCategory(submission.sample_category);
      setShowSubmissionForm(true);
      history.push(buildCreateSampleSubmissionPath(submission.sample_category));
    } else {
      toast.error(message);
    }
    setScheduledActionLoadingId(null);
  };

  const handleDeleteScheduledSubmission = (submission) => {
    setScheduledSubmissionToDelete(submission);
  };

  const handleConfirmDeleteScheduledSubmission = async () => {
    const submission = scheduledSubmissionToDelete;
    if (!submission) return;

    setScheduledDeleteLoading(true);
    let samples;
    if (submission.sample_category === "environment") {
      const { success, result } = await getDraftSubmissionsInfo(submission.submit_id);
      if (success) {
        samples = result.samples;
      }
    }

    const { success, message } = await deleteDraft(submission.submit_id, submission.sample_category);
    if (success) {
      toast.success("Successfully deleted draft");
      setScheduledSubmissionToDelete(null);
      if (showSubmissionDetailsModal) {
        setShowSubmissionDetailsModal(false);
      }
      refreshSubmissionsList();
      if (samples?.length) {
        samples.forEach(({ image_path }) => {
          if (image_path) {
            deleteFileFromS3WithPresigned([image_path], "private");
          }
        });
      }
    } else {
      toast.error(message);
    }
    setScheduledDeleteLoading(false);
  };

  const handleSearchByChange = (field) => {
    setSearchByField(field);
  };

  const handleSubmitByChange = (value) => {
    setSubmitBy(value);
  };

  const handleSampleCategory = (value) => {
    setSampleFilterCategory(value);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
  };

  const handleIssuesFilter = (value) => {
    setIssuesFilter(value);
  };

  const handleDeletedSamplesFilter = (value) => {
    setDeletedSamplesFilter(value);
  };

  /**
   * Call the delete submission api, if successful close the confirm modal and refresh the submissions list.
   * If submissions detail modal is open, close it.
   * @param {Object} submission { submit_id, submit_name, submit_date, po, status }
   */
  const handleConfirmDeleteSubmission = async (submission, fromDeleteSample = false) => {
    setDeleteLoading(true);
    const { success, message } = await deleteSubmission(submission.submit_id, submission.status, submission.sample_category);
    if (success) {
      toast.success(`Successfully deleted${fromDeleteSample ? " sample and" : ""} submission`);
      if (showSubmissionDetailsModal) {
        setShowSubmissionDetailsModal(false);
      }
      refreshSubmissionsList();
      setSubmissionToDelete(null);
      setDeleteLoading(false);
      return true;
    }
    toast.error(fromDeleteSample ? "Failed to delete sample" : message);
    setDeleteLoading(false);
    return false;
  };

  const handleShareSubmission = () => {
    setShareSubmission(false);
  };

  const handleShareSubmissionInfo = (SampleType) => {
    setShareSampleInfo(true);
    setShareSampleType(SampleType);
    setShareSubmission(false);
  };

  const handleShareSubmissionModal = () => {
    setShareSampleInfo(false);
  };

  const handleExportData = () => {
    setExportData(false);
  };

  return (
    <>
      {(submissionToDelete) // delete draft confirmation modal
        && (
          <SampleSubmissionConfirmModal
            headerText="Delete Submission Confirmation"
            bodyText="Are you sure you want to delete this submission? This cannot be undone."
            buttonText={["Cancel", "Delete", "Deleting"]}
            buttonFunctions={[() => setSubmissionToDelete(null), () => handleConfirmDeleteSubmission(submissionToDelete)]}
            loading={deleteLoading}
          />
        )}
      {(scheduledSubmissionToDelete)
        && (
          <SampleSubmissionConfirmModal
            headerText="Delete Draft Confirmation"
            bodyText="Are you sure you want to delete this draft? This cannot be undone."
            buttonText={["Cancel", "Delete", "Deleting"]}
            buttonFunctions={[() => setScheduledSubmissionToDelete(null), handleConfirmDeleteScheduledSubmission]}
            loading={scheduledDeleteLoading}
          />
        )}
      <div className="submitted-submissions-container">
        <div className="SubmissionsHeader">
          <div className="sample-submission-title-block" />
          <h2>Submissions</h2>
          <SubmissionsSearch
            search={search}
            setSearch={setSearch}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            getSubmissions={getSubmissions}
            setSubmittedByLst={setSubmittedByLst}
            submittedByLst={submittedByLst}
            setSearchField={handleSearchByChange}
            loadingState={(loadingFields || loadingSubmissions)}
            setSubmitBy={handleSubmitByChange}
            setSampleFilterCategory={handleSampleCategory}
            setStatusFilter={handleStatusFilter}
            setIssuesFilter={handleIssuesFilter}
            setDeletedSamplesFilter={handleDeletedSamplesFilter}
            searchByField={searchByField}
            setSearchByField={setSearchByField}
            setShowUpdateOnboardingModal={setShowUpdateOnboardingModal}
            filterTagsContainer={filterTagsContainer}
          />
        </div>
        <div ref={setFilterTagsContainer} className="SubmissionsFilterTagsRow" />
        <StyledScrollbar
          ref={submittedSubmissionsScrollbarRef}
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
          onScrollStop={(e) => handleScrollToBottom(e)}
        >
          <div className="submitted-submissions-table-container">
            <table>
              <thead className="SubmissionsTable__Header">
                <tr>
                  <th className="submitted-submissions-expand-th" aria-label="Expand" />
                  <th>Status</th>
                  <th>Submission Name</th>
                  <th>PO Number</th>
                  <th className="submitted-submissions-type-th">Type</th>
                  <th className="submitted-submissions-table-submitted-on-th">Last Action On</th>
                  <th className="submitted-submissions-table-submitted-on-th">Last Action By</th>
                  {/* <th className="submitted-submissions-table-submitted-on-th">Submitted on</th>
                  <th className="submitted-submissions-table-submitted-on-th">Submitted by</th> */}
                  <th className="submitted-submissions-table-samples-number-th">
                    Samples
                  </th>
                  <th className="submitted-submissions-table-samples-number-th">Total Tests</th>
                  <th className="submitted-submissions-table-sample-issue-th">Sample Issue</th>
                  {/* <th className="submitted-submissions-table-actions-th">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {!(loadingFields || loadingSubmissions) && submittedSubmissions.map((submission, idx) => {
                  const isMultiIssue = isExpandableSampleIssues(submission);
                  const isExpanded = expandedIssueSubmissionId === submission.submit_id;
                  const issueSamples = isExpanded ? getSamplesWithIssues(submission) : [];

                  return (
                    <React.Fragment key={submission.submit_id || idx}>
                      <SubmittedSubmissionRow
                        submission={submission}
                        search={search}
                        handleClickSubmission={handleClickSubmission}
                        handleDeleteSubmission={handleDeleteSubmission}
                        handleAddSamples={handleAddSamples}
                        hideActions
                        showSampleIssue
                        showExtendedColumns
                        showSubmissionType
                        hasExpandColumn
                        isExpandable={isMultiIssue}
                        isExpanded={isExpanded}
                        onToggleExpand={handleToggleIssueExpand}
                        onOpenSampleIssue={handleOpenSampleIssue}
                        onOpenExpiryFlow={handleOpenExpiryFlow}
                        onOpenActivityHistory={handleOpenActivityHistory}
                        searchField={searchByField}
                      />
                      {isMultiIssue && isExpanded && issueSamples.map((issueSample) => (
                        <SubmittedSubmissionIssueChildRow
                          key={getIssueSampleUniqueIdentifier(issueSample) || `${issueSample.sample_id}-${issueSample.issue_type}`}
                          submission={submission}
                          issueSample={issueSample}
                          showExtendedColumns
                          showSubmissionType
                          showSampleIssue
                          hasExpandColumn
                          onIssueSampleClick={handleOpenSampleIssue}
                        />
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            {!(loadingFields || loadingSubmissions) && submittedSubmissions.length === 0 && (<NoResultsGIF image={pumpkinGIF} className="submitted-submissions-no-results" message="No Data" />)}
            {(loadingFields || loadingSubmissions) && (<div className="submitted-submissions-table-skeleton ActiveloadingAnimation" />)}
          </div>
          {loadingNewContent && !(loadingFields || loadingSubmissions) && <Loading />}
        </StyledScrollbar>
        <div className="Submissions_FloatingButton">
          <FloatingButton
            setShareSubmission={setShareSubmission}
            setExportData={setExportData}
            setShowSubmissionForm={setShowSubmissionForm}
            setSampleCategory={setSampleCategory}
          />
        </div>
      </div>
      {showSubmissionDetailsModal && (
        <SubmissionDetailsModal
          showSubmissionDetailsModal={showSubmissionDetailsModal}
          setShowSubmissionDetailsModal={setShowSubmissionDetailsModal}
          sampleLinkFieldsConfig={sampleLinkFieldsConfig}
          sampleFields={sampleFields}
          sampleFieldsInfo={sampleFieldsInfo}
          submissionClicked={submissionClicked}
          handleDeleteSubmission={handleDeleteSubmission}
          handleEditSample={handleEditSample}
          handleAddSamples={handleAddSamples}
          handleConfirmDeleteSubmission={handleConfirmDeleteSubmission}
          refreshSubmissionsList={refreshSubmissionsList}
          onEditScheduledSubmission={handleEditScheduledSubmission}
          onDeleteScheduledSubmission={handleDeleteScheduledSubmission}
          scheduledActionLoadingId={scheduledActionLoadingId}
        />
      )}
      {showShippedFlowModal && (
        <SubmissionShippedFlowModal
          open={showShippedFlowModal}
          submission={shippedFlowSubmission}
          onClose={() => {
            setShowShippedFlowModal(false);
            setShippedFlowSubmission(null);
          }}
          onSuccess={refreshSubmissionsList}
        />
      )}
      {showActivityHistoryModal && (
        <ActivityHistoryModal
          open={showActivityHistoryModal}
          submission={activityHistorySubmission}
          onClose={() => {
            setShowActivityHistoryModal(false);
            setActivityHistorySubmission(null);
          }}
        />
      )}
      {showSampleIssueActionModal && (
        <SampleIssueActionModal
          open={showSampleIssueActionModal}
          submission={sampleIssueFlowSubmission}
          issueSample={sampleIssueFlowSample}
          onClose={() => {
            setShowSampleIssueActionModal(false);
            setSampleIssueFlowSubmission(null);
            setSampleIssueFlowSample(null);
          }}
          onEditSample={handleEditIssueSample}
          onSuccess={handleIssueFlowSuccess}
          prepareIssueSampleForResolve={enrichIssueSampleForResolve}
        />
      )}
      {
        shareSampleInfo && hasPermission(permissions.sampleSubmission.share) && (
          <InviteModal handleShowInvite={handleShareSubmissionModal} type={shareSampleType} />
        )
      }
      {
        shareSubmission && hasPermission(permissions.sampleSubmission.share) && (
          <SampleShareModal handleShareSubmissionInfo={handleShareSubmissionInfo} onCancel={handleShareSubmission} />
        )
      }
      {
        exportData && canExportSampleSubmissionData() && !areResultsLocked && (
          <ExportDataModal
            onCancel={handleExportData}
            heading="Export Submission Data"
          />
        )
      }
    </>
  );
}
